/**
 * The browser side of platform/src/converters.ts.
 *
 * `firestoreConverter` is the ONLY way a document becomes a domain object in
 * this app. It builds the object from a mapping function, so the field-by-field
 * rule is structural rather than a thing to remember: there is no `snap.data()`
 * in reach of a component to spread.
 */
import { parseDoc } from '@puppy-doctor/platform';
import type {
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import type { z } from 'zod';

export type FromFirestore<T> = (id: string, data: DocumentData) => T;

export const firestoreConverter = <T extends z.ZodType>(
  schema: T,
  fromDoc: FromFirestore<z.infer<T>>,
): FirestoreDataConverter<z.infer<T>> => ({
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) =>
    parseDoc(schema, snap.ref.path, fromDoc(snap.id, snap.data(options))),

  /**
   * Writes go through explicit `setDoc`/`updateDoc` payloads built by the
   * mutation that makes them, not through a converter. A `toFirestore` that
   * round-trips the domain type looks symmetrical and is a trap: it turns a
   * partial update into a full overwrite, and the field it silently clears is
   * the one another device wrote thirty seconds ago.
   */
  toFirestore: () => {
    throw new Error('Write through an explicit payload, never through the converter.');
  },
});

/**
 * `estimate`, deliberately.
 *
 * When a write is still queued, its `serverTimestamp()` fields have no value
 * yet. The default (`'none'`) reads them as null, which makes a just-saved
 * record sort to the beginning of time and jump when the sync lands. `estimate`
 * fills in the local clock instead, so the row stays where the user put it.
 *
 * This is why the domain schemas use client-set `recordedAt` for anything the UI
 * *orders* by, and leave `serverTimestamp()` for sync metadata alone.
 */
export const SNAPSHOT_OPTIONS: SnapshotOptions = { serverTimestamps: 'estimate' };
